import { getInitials } from '@/utils/initials';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDurationWords, formatRelativeTime } from '@/i18n/locale';
import { Phone, Mail, MessageSquare, Users, Activity, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';

/**
 * L'historique des echanges du client, tel que le CRM le detient.
 *
 * L'onglet montrait les appels passes depuis Revcall. Un echange tenu par
 * quelqu'un de l'equipe Swoft, un e-mail, une reunion, n'y apparaissait pas :
 * l'ecran annoncait « l'activite du CRM » en montrant la moitie qui vient de
 * nous. Les entrees viennent maintenant de Swoft, les notres comprises,
 * puisque chaque appel qualifie y depose une Communication.
 */

interface SwoftCommunication {
  communicationId: string;
  channel?: string;
  direction?: string;
  subject?: string;
  contentPreview?: string;
  fromDisplayName?: string;
  outcome?: string;
  durationSeconds?: number;
  occurredAt?: string;
}

const CANAUX: Record<string, { Icon: typeof Phone; bg: string; color: string }> = {
  phone: { Icon: Phone, bg: 'bg-sky-50', color: 'text-sky-600' },
  email: { Icon: Mail, bg: 'bg-violet-50', color: 'text-violet-600' },
  sms: { Icon: MessageSquare, bg: 'bg-emerald-50', color: 'text-emerald-600' },
  chat: { Icon: MessageSquare, bg: 'bg-emerald-50', color: 'text-emerald-600' },
  meeting: { Icon: Users, bg: 'bg-amber-50', color: 'text-amber-600' },
};

function canal(c: SwoftCommunication) {
  return CANAUX[(c.channel || '').toLowerCase()] || { Icon: Activity, bg: 'bg-muted', color: 'text-muted-foreground' };
}

/** Ce qu'on lit en premier sur la ligne : la phrase, sinon l'objet. Jamais un
 *  identifiant, jamais une ligne vide. */
function titre(c: SwoftCommunication, defaut: string): string {
  return c.subject || c.contentPreview || defaut;
}

export default function SwoftActivityFeed({
  communications,
  loading,
}: {
  communications: SwoftCommunication[];
  loading?: boolean;
}) {
  const { t } = useTranslation(['pages', 'common', 'dialer']);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
        {t('common:actions.loading')}
      </div>
    );
  }

  if (!communications.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Activity className="w-10 h-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-muted-foreground/60">{t('pages:crm.activity.empty')}</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border divide-y divide-border/60">
      {communications.map((c) => {
        const { Icon, bg, color } = canal(c);
        const entrant = (c.direction || '').toLowerCase() === 'inbound';
        const Fleche = entrant ? ArrowDownLeft : ArrowUpRight;
        return (
          <div key={c.communicationId} className="flex items-start gap-3 px-4 py-3 min-w-0">
            <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${bg}`}>
              <Icon className={`w-[18px] h-[18px] ${color}`} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <Fleche className={`w-3.5 h-3.5 shrink-0 ${entrant ? 'text-emerald-600' : 'text-muted-foreground/60'}`} />
                <p className="text-sm font-medium text-foreground truncate">
                  {titre(c, t('pages:crm.activity.untitled'))}
                </p>
              </div>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground min-w-0">
                {c.fromDisplayName && (
                  <span className="flex items-center gap-1 min-w-0">
                    <span className="w-4 h-4 rounded-full bg-muted flex items-center justify-center text-[9px] font-semibold text-muted-foreground shrink-0">
                      {getInitials(c.fromDisplayName)}
                    </span>
                    <span className="truncate">{c.fromDisplayName}</span>
                  </span>
                )}
                {c.durationSeconds ? <span>{formatDurationWords(c.durationSeconds)}</span> : null}
                {/* Le code brut de Swoft (« meeting_booked ») est celui que
                    Revcall lui a envoye : on le reaffiche avec le libelle du
                    produit, jamais tel quel. */}
                {c.outcome && <span className="truncate">{outcomeLabel(c.outcome, t)}</span>}
              </div>
            </div>
            {c.occurredAt && (
              <span className="text-xs text-muted-foreground/80 shrink-0" title={formatDateTime(c.occurredAt)}>
                {formatRelativeTime(c.occurredAt)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
